// The installer scope sheet's arithmetic, kept apart from the screen so it can be tested: which ticked rows become the
// tasks a crew is sent, at what figure, and whether the sheet is ready to send.
import {createT} from '@aiw/i18n';
import {scopeTotal, type ScopeTask, type ScopeTaskMode} from '@artemis-group/aiw-contracts';

export type DraftTask = ScopeTask & {status: string; recorded: boolean; rate_source?: 'book' | 'standard'};
export type ScopeRow = {task: DraftTask; picked: boolean; mode: ScopeTaskMode; unit: string; fee: string; label_local: string};

const r2 = (n: number) => Math.round(n * 100) / 100;
const num = (v: string) => { const n = Number(v); return v.trim() !== '' && Number.isFinite(n) && n >= 0 ? n : null; };

/** Every line ticked, at the figure the draft proposed (the crew's book, a fee already recorded, or please quote). */
export function initialRows(tasks: DraftTask[]): ScopeRow[] {
  return tasks.map((task) => ({task, picked: true, mode: task.mode, unit: task.unit_aud !== null ? String(task.unit_aud) : '', fee: task.fee_aud !== null && task.unit_aud === null ? String(task.fee_aud) : '', label_local: task.label_local}));
}

/** A row's fee as the crew will read it; null for please quote, own crew, or a figure not typed yet. */
export function rowFee(row: ScopeRow): number | null {
  if (row.mode === 'included') return 0;
  if (row.mode === 'quote' || row.mode === 'own_crew') return null;
  const q = row.task.quantity;
  if (row.mode === 'book') return row.task.unit_aud !== null && q !== null ? r2(row.task.unit_aud * q) : row.task.fee_aud;
  const unit = num(row.unit);
  if (unit !== null && q !== null) return r2(unit * q);
  return num(row.fee);
}

/** The ticked rows as the command takes them. A Manual row with neither a rate nor a fee is left out of `ready`. */
export function rowsToTasks(rows: ScopeRow[]): {tasks: ScopeTask[]; ready: boolean; missingFee: boolean} {
  const picked = rows.filter((r) => r.picked);
  let missingFee = false;
  const tasks = picked.map<ScopeTask>((r) => {
    const fee = rowFee(r);
    if ((r.mode === 'manual' || r.mode === 'book') && fee === null) missingFee = true;
    const unit = r.mode === 'book' ? r.task.unit_aud : r.mode === 'manual' && r.task.quantity !== null ? num(r.unit) : null;
    return {line_id: r.task.line_id, label: r.task.label, label_local: r.label_local.trim(), quantity: r.task.quantity, uom: r.task.uom, mode: r.mode,
      unit_aud: r.mode === 'book' || r.mode === 'manual' ? unit : null, fee_aud: r.mode === 'quote' || r.mode === 'own_crew' ? null : fee,
      rate_service_id: r.mode === 'book' ? r.task.rate_service_id : null};
  });
  return {tasks, ready: tasks.length > 0 && !missingFee, missingFee};
}

export const money = (n: number) => `$${n.toLocaleString('en-AU', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

/** A few words for the message: each task's first phrase, without quantities, at most four. */
export function scopeSummary(tasks: ScopeTask[], lang: 'en' | 'zh'): string {
  const w = createT(lang);
  const words = [...new Set(tasks.map((t) => (lang === 'zh' && t.label_local ? t.label_local : t.label).split(/\s+·\s+|:\s+|\s+-\s+/)[0]!.trim()).filter(Boolean))];
  const shown = words.slice(0, 4).join(w('scope.msg.join'));
  return words.length > 4 ? `${shown} ${w('scope.msg.more')}` : shown;
}

/** The short message that carries the link — the same text by text message or by EmmA in WeChat. {link} stays as written.
 *  Jacky, 26 October 2026: "in announcement, print out the job value too, and details to be viewed in URL" — the crew's
 *  pay for the Job (their own rates, ex GST, never the customer's price) replaces the task list; the detail is the link. */
export function scopeMessage(input: {lang: 'en' | 'zh'; crew: string; job_ref: string; suburb: string; tasks: ScopeTask[]; estimated_days: number | null; wechat: boolean; own?: boolean}): string {
  const w = createT(input.lang);
  const totals = scopeTotal(input.tasks);
  const quotes = totals.missing;
  const pay = input.own ? null
    : totals.known > 0 && quotes ? w('scope.msg.payPlusQuote', {amount: money(totals.known), count: String(quotes)})
    : totals.known > 0 ? w('scope.msg.pay', {amount: money(totals.known)})
    : quotes ? w('scope.msg.quoteOnly', {count: String(quotes)}) : null;
  return [
    w('scope.msg.hello', {crew: input.crew}),
    [input.job_ref, input.suburb].filter(Boolean).join(' · '),
    ...(pay ? [pay] : []),
    ...(input.estimated_days ? [w('scope.msg.days', {days: String(input.estimated_days)})] : []),
    quotes ? w('scope.msg.quote') : w('scope.msg.open'),
    '{link}',
    w(input.wechat ? 'scope.msg.signEmma' : 'scope.msg.sign'),
  ].join('\n');
}
